<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Cancel Subscription
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="space-y-4">
                <p>Cancelling this subscription will immediately update the expiry date to the current time and deactivate it. Do you want to proceed?</p>

                <UCheckbox
                    v-if="subscription?.source_type === SubscriptionSourceType.credential"
                    v-model="clearCredentialUsage"
                    label="Clear credential usage"
                    :ui="{ label: 'text-sm text-gray-700' }"
                />
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white">
                        Cancel
                    </UButton>
                    <UButton @click="confirm()" size="lg" :loading="confirming" color="red">
                        Confirm
                    </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Subscription } from '~/types/subscription';
import { SubscriptionSourceType } from '~/types/subscription';
import { UserRole } from '~/types/user';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    subscription?: Subscription
}>()

const toast = useToast()
const auth = useAuthStore()

const confirming = ref<boolean>(false)
const clearCredentialUsage = ref<boolean>(false)

async function confirm() {
    if (!auth.hasRole(UserRole.admin)) {
        toast.add({
            title: 'You do not have permission to cancel subscriptions',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    if (props.subscription?.id) {
        confirming.value = true

        try {
            await $fetch(`/api/subscriptions/${props.subscription.id}`, {
                method: 'DELETE',
                query: {
                    clearCredentialUsage: clearCredentialUsage.value
                }
            })

            toast.add({
                title: 'Subscription cancelled successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('reload')
        } catch (error: any) {
            toast.add({
                title: 'Could not cancel subscription!',
                description: 'Please try again later.',
                color: 'red',
                icon: 'i-ph-x-circle-duotone'
            })
        } finally {
            confirming.value = false
            clearCredentialUsage.value = false
            dismiss()
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>